import React, { useCallback, useEffect, useRef, useState } from 'react';
import Button from '../../../components/common/Button';
import { agentApi } from '../../../services/agentApi';
import { AgentNote } from '../../../types/interfaces';
import { formatDateTime } from '../../../utils/dates';
import { errorDetail } from './AgentSettings';
import './agent.css';

/** Comma-separated tags to a list (blank entries dropped). */
export function parseTags(value: string): string[] {
  return value.split(',').map((t) => t.trim()).filter(Boolean);
}

type Draft = { text: string; tags: string };

const NoteForm: React.FC<{
  initial: Draft; submitLabel: string; onSubmit(d: Draft): Promise<void>; onCancel?(): void;
}> = ({ initial, submitLabel, onSubmit, onCancel }) => {
  const [draft, setDraft] = useState(initial);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!draft.text.trim()) return;
    setBusy(true);
    setError('');
    try {
      await onSubmit(draft);
      if (!onCancel) setDraft({ text: '', tags: '' });
    } catch (err) {
      setError(errorDetail(err, 'Could not save the note'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="agent-form" onSubmit={submit}>
      <label className="agent-field">
        <span>Note</span>
        <textarea className="input input--default input--sm agent-plan__revise-input" rows={3} maxLength={5000}
          value={draft.text} onChange={(e) => setDraft({ ...draft, text: e.target.value })} />
      </label>
      <label className="agent-field">
        <span>Tags (comma-separated, up to 20)</span>
        <input className="input input--default input--sm" value={draft.tags}
          onChange={(e) => setDraft({ ...draft, tags: e.target.value })} />
      </label>
      {error && <div className="agent-inline-error" role="alert">{error}</div>}
      <div className="agent-plan__buttons">
        <Button variant="primary" size="sm" type="submit" disabled={busy || !draft.text.trim()}>{submitLabel}</Button>
        {onCancel && <Button variant="ghost" size="sm" onClick={onCancel}>Cancel</Button>}
      </div>
    </form>
  );
};

/** Standing notes the agent reads on every turn: list, add, edit and deactivate. */
const AgentNotes: React.FC = () => {
  const [notes, setNotes] = useState<AgentNote[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<number | null>(null);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  const load = useCallback(async () => {
    try {
      const list = await agentApi.listNotes();
      if (mounted.current) {
        setNotes(list);
        setError('');
      }
    } catch (err) {
      if (mounted.current) setError(errorDetail(err, 'Could not load the notes'));
    } finally {
      if (mounted.current) setLoaded(true);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const add = async (d: Draft) => {
    await agentApi.createNote(d.text.trim(), parseTags(d.tags));
    await load();
  };

  const update = async (id: number, d: Draft) => {
    await agentApi.updateNote(id, d.text.trim(), parseTags(d.tags));
    if (mounted.current) setEditing(null);
    await load();
  };

  const deactivate = async (id: number) => {
    try {
      await agentApi.deleteNote(id);
      await load();
    } catch (err) {
      if (mounted.current) setError(errorDetail(err, 'Could not deactivate the note'));
    }
  };

  return (
    <div className="agent-panel">
      <section aria-label="Add a note">
        <h3 className="agent-panel__title">Add a note</h3>
        <p className="agent-meta">Notes are standing instructions or facts the agent reads before every answer.</p>
        <NoteForm initial={{ text: '', tags: '' }} submitLabel="Add note" onSubmit={add} />
      </section>
      <section aria-label="Active notes">
        <h3 className="agent-panel__title">Active notes</h3>
        {error && <div className="agent-inline-error" role="alert">{error}</div>}
        {loaded && !error && notes.length === 0 && <p className="agent-meta">No notes yet.</p>}
        <ul className="agent-plain-list">
          {notes.map((n) => (
            <li key={n.id} className="agent-note-item">
              {editing === n.id ? (
                <NoteForm initial={{ text: n.text, tags: (n.tags || []).join(', ') }} submitLabel="Save"
                  onSubmit={(d) => update(n.id, d)} onCancel={() => setEditing(null)} />
              ) : (
                <>
                  <div className="agent-msg__text">{n.text}</div>
                  {n.tags && n.tags.length > 0 && (
                    <div className="agent-attachments">
                      {n.tags.map((t) => <span key={t} className="agent-file-chip">{t}</span>)}
                    </div>
                  )}
                  <div className="agent-meta">
                    {n.created_by || 'unknown'} · updated {formatDateTime(n.updated_at || n.created_at)}
                  </div>
                  <span className="agent-conv__actions">
                    <button type="button" className="agent-link-button" onClick={() => setEditing(n.id)}>Edit</button>
                    <button type="button" className="agent-link-button" onClick={() => deactivate(n.id)}>Deactivate</button>
                  </span>
                </>
              )}
            </li>
          ))}
        </ul>
      </section>
    </div>
  );
};

export default AgentNotes;
